// src\themes\lino\components\BannerSlider.tsx
"use client";

import { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import { SliderItemFrom } from "@/core/models/slider-model";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Image from "next/image";
import { cn } from "@/core/lib/utils";
import Link from "next/link";
import { motion, AnimatePresence } from "motion/react";
import { loadThemeComponent } from "@/core/config/theme-component-loader";
import { textGroupVariants, textItemVariants } from "./animations/textReveal";
const CustomButton = loadThemeComponent("CustomButton")

type SliderItemMediaProps = {
  url: string;
  alt?: string;
  className?: string;
  videoRef?: (el: HTMLVideoElement | null) => void;
};

const isVideoUrl = (url?: string) => {
  if (!url) return false;
  return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
};

function SliderMedia({ url, alt, className, videoRef }: SliderItemMediaProps) {
  if (isVideoUrl(url)) {
    return (
      <video
        ref={videoRef}
        src={url}
        autoPlay
        muted
        loop
        playsInline
        className={cn("absolute inset-0 w-full h-full object-cover z-0", className)}
      />
    );
  }

  return <Image src={url} alt={alt || ""} fill className={className} priority />;
}

type BannerSliderProps = {
  slides: SliderItemFrom[];
  varient?: "full" | "short";
  onlyDesktop?: boolean;
};

export default function BannerSlider({ slides, varient = "full", onlyDesktop = false }: BannerSliderProps) {
  const videoRefs = useRef<Map<number, Set<HTMLVideoElement>>>(new Map());
  const [activeIndex, setActiveIndex] = useState(0);


  const registerVideoRef = (index: number) => (el: HTMLVideoElement | null) => {
    const map = videoRefs.current;
    let set = map.get(index);
    if (!set) {
      set = new Set();
      map.set(index, set);
    }
    if (el) set.add(el);
  };

  const playVideoForSlide = (index: number) => {
    const set = videoRefs.current.get(index);
    if (!set) return;
    set.forEach((video) => {
      if (video.paused) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => { });
        }
      }
    });
  };

  const handleActiveSlideVideos = (swiper: SwiperClass) => {
    setActiveIndex(swiper.realIndex);
    playVideoForSlide(swiper.realIndex);
  };

  if (!slides?.length) return null;

  return (
    <div className="relative sm:px-3 lg:px-7  lg:pt-6">
      <Swiper
        modules={[Navigation, Autoplay]}
        spaceBetween={0}
        slidesPerView={1}
        navigation={{
          nextEl: ".swiper-button-next-custom",
          prevEl: ".swiper-button-prev-custom",
        }}
        loop
        autoplay={{ delay: 5500, disableOnInteraction: false }}
        onSwiper={handleActiveSlideVideos}
        onSlideChange={handleActiveSlideVideos}
        className="w-full"
      >
        {slides.map((slide, index) => {
          const isSlideVideo =
            isVideoUrl(slide.image_info?.url) || isVideoUrl(slide.image_mobile_info?.url);
          const slideAutoplayDelay = isSlideVideo ? 15000 : 5500;

          const ImageElement = (() => {
            if (onlyDesktop) {
              return (
                slide.image_info?.url && (
                  <SliderMedia
                    url={slide.image_info.url}
                    alt={slide.image_info.description || ""}
                    className="w-full h-full object-cover z-0 absolute"
                    videoRef={registerVideoRef(index)}
                  />
                )
              );
            }

            return (
              <>
                <div className="hidden lg:flex h-full w-full">
                  {slide.image_info?.url && (
                    <SliderMedia
                      url={slide.image_info.url}
                      alt={slide.image_info.description || ""}
                      className="w-full h-full object-cover z-0 absolute"
                      videoRef={registerVideoRef(index)}
                    />
                  )}
                </div>
                <div className="flex lg:hidden h-full w-full">
                  {slide.image_mobile_info?.url && (
                    <SliderMedia
                      url={slide.image_mobile_info.url}
                      alt={slide.image_mobile_info.description || ""}
                      className="w-full h-full object-cover z-0 absolute"
                      videoRef={registerVideoRef(index)}
                    />
                  )}
                </div>
              </>
            );
          })();

          const Content = (
            <div
              className={cn(
                "relative bg-primary-100 overflow-hidden rounded-2xl",
                varient === "full" ? "h-[88vh] lg:h-[94vh]" : "aspect-[16/9]"
              )}
            >
              {ImageElement}
              <div className="absolute inset-0 bg-gradient-to-b from-primary-950/40 via-transparent to-primary-950/30 z-0" />

              <div className="absolute inset-0 z-10 flex items-start justify-center">
                <div className="w-full px-6 pt-28 lg:pt-40 text-center">
                  <AnimatePresence mode="wait">
                    {activeIndex === index && (
                      <motion.div
                        key={`slide-content-${index}`}
                        className="max-w-2xl mx-auto space-y-5"
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={textGroupVariants}
                      >
                        {slide.title && (
                          <motion.span
                            variants={textItemVariants}
                            className="block text-primary-0 text-[11px] tracking-[0.32em] uppercase"
                          >
                            {slide.title}
                          </motion.span>
                        )}
                        {slide.description && (
                          <motion.h1
                            variants={textItemVariants}
                            className="text-primary-0 text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-[-0.02em]"
                          >
                            <span className="font-primary-bold">{slide.description.split(' ').slice(0, -1).join(' ')}</span>{' '}
                            <span className="italic font-light">{slide.description.split(' ').slice(-1)}</span>
                          </motion.h1>
                        )}
                        {slide.buttonContent && (
                          <motion.div
                            variants={textItemVariants}
                            className="pt-5"
                          >
                            <CustomButton size="lg" variant="transparentWhite">
                              {slide.buttonContent}
                            </CustomButton>
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          );

          return (
            <SwiperSlide key={slide.priority} data-swiper-autoplay={slideAutoplayDelay}>
              {slide.link ? <Link href={slide.link}>{Content}</Link> : Content}
            </SwiperSlide>
          );
        })}
      </Swiper>

      {slides.length > 1 && (
        <>

          <div className="absolute z-20 bottom-8 lg:bottom-10 ltr:left-6 rtl:right-6 lg:ltr:left-16 lg:rtl:right-16 flex items-center gap-2">
            {slides.map((_, i) => (
              <span
                key={i}
                className={cn(
                  "h-px transition-all duration-500 bg-primary-0/45",
                  activeIndex === i ? "w-10 bg-primary-0" : "w-4"
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}